import { useState } from 'react'
import { QueryClient, QueryClientProvider, useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { Switch } from '@headlessui/react'
import { ChartBarIcon, ShieldCheckIcon, ChartPieIcon, AcademicCapIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import Tippy from '@tippyjs/react'
import 'tippy.js/dist/tippy.css'
import 'tippy.js/themes/light.css'

const queryClient = new QueryClient()
const WORKER_URL = import.meta.env.VITE_WORKER_URL || 'https://linkedin-scraper.your-worker.workers.dev'

interface ScrapeOptions {
  includeConnections: boolean;
  includeExperience: boolean;
  includeEducation: boolean;
  includeSkills: boolean;
  includeRecommendations: boolean;
  includeActivityPosts: boolean;
  includeContactInfo: boolean;
}

interface ProxyOptions {
  useProxy: boolean;
  proxyUrl: string;
}

interface SearchOptions {
  location?: string;
  currentCompany?: string;
  industry?: string;
  maxResults: number;
}

interface TooltipProps {
  content: string;
  children: React.ReactNode;
}

const InfoTooltip = ({ content, children }: TooltipProps) => (
  <Tippy
    content={content}
    theme="light"
    arrow={true}
    placement="right"
    className="text-sm p-2 bg-white shadow-lg rounded-lg border border-gray-200"
  >
    <div className="inline-flex items-center">
      {children}
      <InformationCircleIcon className="h-4 w-4 ml-1 text-gray-400 hover:text-gray-600" />
    </div>
  </Tippy>
);

const tooltips = {
  searchQuery: "Enter keywords to search for LinkedIn profiles. Example: 'Uber Driver', 'Software Engineer', etc.",
  url: "Enter a specific LinkedIn profile URL to scrape a single profile.",
  cookie: "The 'li_at' cookie from your LinkedIn account. To get this:\n1. Go to LinkedIn.com and make sure you're logged in\n2. Right-click anywhere on the page and select 'Inspect' (or press F12)\n3. Go to the 'Application' or 'Storage' tab in developer tools\n4. Under 'Cookies', find 'www.linkedin.com'\n5. Look for a cookie named 'li_at'\n6. Copy the entire value of this cookie\nThis cookie is required for authentication and allows the scraper to access LinkedIn data through your account.",
  location: "Filter profiles by location. Can be city, state, or country.",
  currentCompany: "Filter profiles by their current company.",
  industry: "Filter profiles by industry sector.",
  maxResults: "Maximum number of profiles to scrape (1-100).",
  includeConnections: "Scrape the profile's connections list (may increase scraping time).",
  includeExperience: "Include work experience history in results.",
  includeEducation: "Include education history in results.",
  includeSkills: "Include skills and endorsements in results.",
  includeRecommendations: "Include recommendations received by the profile.",
  includeActivityPosts: "Include recent activity and posts (may increase scraping time).",
  includeContactInfo: "Attempt to extract contact information if available.",
  useProxy: "Use a proxy server to avoid rate limiting (recommended for bulk scraping).",
  proxyUrl: "Your proxy server URL (optional). Format: http://proxy.example.com:8080",
};

function LinkedInScraper() {
  const [searchQuery, setSearchQuery] = useState('')
  const [url, setUrl] = useState('')
  const [cookie, setCookie] = useState('')
  const [results, setResults] = useState<any[]>([])
  const [searchOptions, setSearchOptions] = useState<SearchOptions>({
    location: '',
    currentCompany: '',
    industry: '',
    maxResults: 10
  })
  const [scrapeOptions, setScrapeOptions] = useState<ScrapeOptions>({
    includeConnections: false,
    includeExperience: true,
    includeEducation: true,
    includeSkills: true,
    includeRecommendations: false,
    includeActivityPosts: false,
    includeContactInfo: true,
  })
  const [proxyOptions, setProxyOptions] = useState<ProxyOptions>({
    useProxy: false,
    proxyUrl: '',
  })
  const [exportFormat, setExportFormat] = useState<'json' | 'csv'>('json')
  const [selectedProfile, setSelectedProfile] = useState<number | null>(null)

  const mutation = useMutation({
    mutationFn: async (data: { 
      searchQuery?: string;
      url?: string;
      cookie: string; 
      searchOptions: SearchOptions;
      scrapeOptions: ScrapeOptions;
      proxyOptions: ProxyOptions;
    }) => {
      const response = await axios.post(WORKER_URL, data)
      return response.data
    },
    onSuccess: (data) => {
      setResults(data.results)
      setSelectedProfile(data.results.length > 0 ? 0 : null)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    mutation.mutate({ 
      searchQuery: searchQuery || undefined,
      url: url || undefined,
      cookie, 
      searchOptions, 
      scrapeOptions, 
      proxyOptions 
    })
  }

  const handleExport = () => {
    if (!results.length) return

    if (exportFormat === 'csv') {
      const csvContent = convertToCSV(results)
      downloadFile(csvContent, 'profile-data.csv', 'text/csv')
    } else {
      const jsonContent = JSON.stringify(results, null, 2)
      downloadFile(jsonContent, 'profile-data.json', 'application/json')
    }
  }

  const downloadFile = (content: string, filename: string, type: string) => {
    const blob = new Blob([content], { type })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const convertToCSV = (data: any[]): string => {
    // CSV format focused on email data
    const headers = ['Name', 'Title', 'Company', 'Location', 'Email']
    const rows = data.map(profile => {
      const currentCompany = profile.experience?.[0]?.company || ''
      const email = profile.contactInfo?.emails?.[0] || ''
      return [
        profile.name || '',
        profile.title || '',
        currentCompany,
        profile.location || '',
        email
      ]
    })

    // Escape CSV values to handle commas and quotes
    const escapeCSV = (value: string) => {
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`
      }
      return value
    }

    return [
      headers.join(','),
      ...rows.map(row => row.map(escapeCSV).join(','))
    ].join('\n')
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Form */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <InfoTooltip content="Find business emails from LinkedIn profiles based on your search criteria">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">LinkedIn Email Finder</h2>
                </InfoTooltip>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Search Query or URL */}
                  <div className="space-y-4">
                    <div>
                      <InfoTooltip content={tooltips.searchQuery}>
                        <label htmlFor="searchQuery" className="block text-sm font-medium text-gray-700">
                          Search Query (e.g., "Uber Driver")
                        </label>
                      </InfoTooltip>
                      <input
                        type="text"
                        id="searchQuery"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Enter job title, keywords, etc."
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>

                    <div className="relative">
                      <div className="absolute inset-0 flex items-center" aria-hidden="true">
                        <div className="w-full border-t border-gray-300" />
                      </div>
                      <div className="relative flex justify-center">
                        <span className="bg-white px-2 text-sm text-gray-500">OR</span>
                      </div>
                    </div>

                    <div>
                      <InfoTooltip content={tooltips.url}>
                        <label htmlFor="url" className="block text-sm font-medium text-gray-700">
                          Single Profile URL
                        </label>
                      </InfoTooltip>
                      <input
                        type="url"
                        id="url"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://www.linkedin.com/in/..."
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                    </div>

                    <div>
                      <InfoTooltip content={tooltips.cookie}>
                        <label htmlFor="cookie" className="block text-sm font-medium text-gray-700">
                          LinkedIn Cookie (li_at)
                        </label>
                      </InfoTooltip>
                      <input
                        type="text"
                        id="cookie"
                        value={cookie}
                        onChange={(e) => setCookie(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        required
                      />
                    </div>
                  </div>

                  {/* Search Options */}
                  <div className="space-y-4">
                    <InfoTooltip content="Configure search filters to find specific profiles">
                      <h3 className="text-lg font-medium text-gray-900">Search Options</h3>
                    </InfoTooltip>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div>
                        <InfoTooltip content={tooltips.location}>
                          <label htmlFor="location" className="block text-sm font-medium text-gray-700">
                            Location
                          </label>
                        </InfoTooltip>
                        <input
                          type="text"
                          id="location"
                          value={searchOptions.location}
                          onChange={(e) => setSearchOptions(prev => ({ ...prev, location: e.target.value }))}
                          placeholder="City, State, or Country"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                      </div>

                      <div>
                        <InfoTooltip content={tooltips.currentCompany}>
                          <label htmlFor="currentCompany" className="block text-sm font-medium text-gray-700">
                            Current Company
                          </label>
                        </InfoTooltip>
                        <input
                          type="text"
                          id="currentCompany"
                          value={searchOptions.currentCompany}
                          onChange={(e) => setSearchOptions(prev => ({ ...prev, currentCompany: e.target.value }))}
                          placeholder="Company name"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                      </div>

                      <div>
                        <InfoTooltip content={tooltips.industry}>
                          <label htmlFor="industry" className="block text-sm font-medium text-gray-700">
                            Industry
                          </label>
                        </InfoTooltip>
                        <input
                          type="text"
                          id="industry"
                          value={searchOptions.industry}
                          onChange={(e) => setSearchOptions(prev => ({ ...prev, industry: e.target.value }))}
                          placeholder="Industry"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                      </div>

                      <div>
                        <InfoTooltip content={tooltips.maxResults}>
                          <label htmlFor="maxResults" className="block text-sm font-medium text-gray-700">
                            Max Results
                          </label>
                        </InfoTooltip>
                        <input
                          type="number"
                          id="maxResults"
                          value={searchOptions.maxResults}
                          onChange={(e) => setSearchOptions(prev => ({ ...prev, maxResults: parseInt(e.target.value) || 10 }))}
                          min="1"
                          max="100"
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Scraping Options */}
                  <div className="space-y-4">
                    <InfoTooltip content="Choose what information to extract from profiles">
                      <h3 className="text-lg font-medium text-gray-900">Scraping Options</h3>
                    </InfoTooltip>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {Object.entries(scrapeOptions).map(([key, value]) => (
                        <Switch.Group key={key}>
                          <div className="flex items-center">
                            <Switch
                              checked={value}
                              onChange={(checked) => 
                                setScrapeOptions(prev => ({ ...prev, [key]: checked }))}
                              className={`${
                                value ? 'bg-indigo-600' : 'bg-gray-200'
                              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                            >
                              <span
                                className={`${
                                  value ? 'translate-x-6' : 'translate-x-1'
                                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                              />
                            </Switch>
                            <InfoTooltip content={tooltips[key as keyof typeof tooltips]}>
                              <Switch.Label className="ml-3 text-sm text-gray-700">
                                {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                              </Switch.Label>
                            </InfoTooltip>
                          </div>
                        </Switch.Group>
                      ))}
                    </div>
                  </div>

                  {/* Proxy Settings */}
                  <div className="space-y-4">
                    <InfoTooltip content="Configure proxy settings to avoid rate limiting">
                      <h3 className="text-lg font-medium text-gray-900">Proxy Settings</h3>
                    </InfoTooltip>
                    <div className="space-y-4">
                      <Switch.Group>
                        <div className="flex items-center">
                          <Switch
                            checked={proxyOptions.useProxy}
                            onChange={(checked) => 
                              setProxyOptions(prev => ({ ...prev, useProxy: checked }))}
                            className={`${
                              proxyOptions.useProxy ? 'bg-indigo-600' : 'bg-gray-200'
                            } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
                          >
                            <span
                              className={`${
                                proxyOptions.useProxy ? 'translate-x-6' : 'translate-x-1'
                              } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                            />
                          </Switch>
                          <InfoTooltip content={tooltips.useProxy}>
                            <Switch.Label className="ml-3 text-sm text-gray-700">
                              Use Proxy
                            </Switch.Label>
                          </InfoTooltip>
                        </div>
                      </Switch.Group>

                      {proxyOptions.useProxy && (
                        <div>
                          <InfoTooltip content={tooltips.proxyUrl}>
                            <label htmlFor="proxyUrl" className="block text-sm font-medium text-gray-700">
                              Proxy URL (optional)
                            </label>
                          </InfoTooltip>
                          <input
                            type="text"
                            id="proxyUrl"
                            value={proxyOptions.proxyUrl}
                            onChange={(e) => setProxyOptions(prev => ({ ...prev, proxyUrl: e.target.value }))}
                            placeholder="http://proxy.example.com:8080"
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Submit and Export */}
                  <div className="flex items-center space-x-4">
                    <button
                      type="submit"
                      disabled={mutation.isPending || (!searchQuery && !url) || !cookie}
                      className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                      {mutation.isPending ? 'Finding Emails...' : 'Search & Find Emails'}
                    </button>

                    {results.length > 0 && (
                      <div className="flex items-center space-x-2">
                        <select
                          value={exportFormat}
                          onChange={(e) => setExportFormat(e.target.value as 'json' | 'csv')}
                          className="rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                        >
                          <option value="csv">CSV (Emails Only)</option>
                          <option value="json">JSON (Full Data)</option>
                        </select>
                        <button
                          type="button"
                          onClick={handleExport}
                          className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                        >
                          Export {results.length} Results
                        </button>
                      </div>
                    )}
                  </div>
                </form>
              </div>

              {mutation.isError && (
                <div className="p-4 bg-red-50 rounded-md">
                  <p className="text-sm text-red-700">Error: {(mutation.error as Error).message}</p>
                </div>
              )}
            </div>

            {/* Right Column - Results */}
            <div className="space-y-6">
              {results.length > 0 ? (
                <>
                  {/* Results Navigation */}
                  <div className="bg-white rounded-lg shadow-lg p-4">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Search Results ({results.length})</h3>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                      {results.map((profile, index) => (
                        <button
                          key={index}
                          onClick={() => setSelectedProfile(index)}
                          className={`p-2 text-left rounded ${
                            selectedProfile === index 
                              ? 'bg-indigo-50 border-indigo-500' 
                              : 'hover:bg-gray-50 border-transparent'
                          } border`}
                        >
                          <p className="font-medium truncate">{profile.name || 'Unknown'}</p>
                          <p className="text-sm text-gray-500 truncate">{profile.title || 'No title'}</p>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Selected Profile Details */}
                  {selectedProfile !== null && (
                    <>
                      {/* Profile Overview */}
                      <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
                        <div className="flex items-center space-x-4">
                          {results[selectedProfile]?.profileImageUrl && (
                            <img 
                              src={results[selectedProfile]?.profileImageUrl} 
                              alt={results[selectedProfile]?.name} 
                              className="w-20 h-20 rounded-full"
                            />
                          )}
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">{results[selectedProfile]?.name}</h3>
                            <p className="text-gray-600">{results[selectedProfile]?.title}</p>
                            <p className="text-gray-500">{results[selectedProfile]?.location}</p>
                          </div>
                        </div>

                        {results[selectedProfile]?.about && (
                          <div>
                            <h4 className="text-lg font-medium text-gray-900 mb-2">About</h4>
                            <p className="text-gray-700 whitespace-pre-line">{results[selectedProfile]?.about}</p>
                          </div>
                        )}

                        {results[selectedProfile]?.experience?.length > 0 && (
                          <div>
                            <h4 className="text-lg font-medium text-gray-900 mb-2">Experience</h4>
                            <div className="space-y-4">
                              {results[selectedProfile]?.experience.map((exp: any, index: number) => (
                                <div key={index} className="border-l-2 border-gray-200 pl-4">
                                  <h5 className="font-medium text-gray-900">{exp.title}</h5>
                                  <p className="text-gray-600">{exp.company}</p>
                                  <p className="text-gray-500">{exp.duration}</p>
                                  {exp.description && (
                                    <p className="mt-2 text-gray-700">{exp.description}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {results[selectedProfile]?.education?.length > 0 && (
                          <div>
                            <h4 className="text-lg font-medium text-gray-900 mb-2">Education</h4>
                            <div className="space-y-4">
                              {results[selectedProfile]?.education.map((edu: any, index: number) => (
                                <div key={index}>
                                  <h5 className="font-medium text-gray-900">{edu.school}</h5>
                                  <p className="text-gray-600">{edu.degree} • {edu.field}</p>
                                  <p className="text-gray-500">{edu.duration}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {results[selectedProfile]?.skills?.length > 0 && (
                          <div>
                            <h4 className="text-lg font-medium text-gray-900 mb-2">Skills</h4>
                            <div className="flex flex-wrap gap-2">
                              {results[selectedProfile]?.skills.map((skill: string, index: number) => (
                                <span 
                                  key={index}
                                  className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800"
                                >
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                        {results[selectedProfile]?.recommendations?.length > 0 && (
                          <div>
                            <h4 className="text-lg font-medium text-gray-900 mb-2">Recommendations</h4>
                            <div className="space-y-4">
                              {results[selectedProfile]?.recommendations.map((rec: string, index: number) => (
                                <blockquote key={index} className="border-l-4 border-gray-200 pl-4 italic text-gray-700">
                                  {rec}
                                </blockquote>
                              ))}
                            </div>
                          </div>
                        )}

                        {results[selectedProfile]?.activityPosts?.length > 0 && (
                          <div>
                            <h4 className="text-lg font-medium text-gray-900 mb-2">Recent Activity</h4>
                            <div className="space-y-4">
                              {results[selectedProfile]?.activityPosts.map((post: any, index: number) => (
                                <div key={index} className="bg-gray-50 p-4 rounded-lg">
                                  <p className="text-gray-700">{post.content}</p>
                                  <div className="mt-2 text-sm text-gray-500">
                                    <span>{post.timestamp}</span>
                                    <span className="mx-2">•</span>
                                    <span>{post.reactions} reactions</span>
                                    <span className="mx-2">•</span>
                                    <span>{post.comments} comments</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Analysis Section */}
                      <div className="bg-white rounded-lg shadow-lg p-6 space-y-6">
                        <h3 className="text-xl font-bold text-gray-900">Profile Analysis</h3>

                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                          {/* Experience Level */}
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center mb-2">
                              <ChartBarIcon className="h-5 w-5 text-indigo-500 mr-2" />
                              <h4 className="text-lg font-medium text-gray-900">Experience Level</h4>
                            </div>
                            <p className="text-2xl font-bold text-indigo-600">{results[selectedProfile]?.analysis.experienceLevel}</p>
                            <p className="text-sm text-gray-500">Career Duration: {results[selectedProfile]?.analysis.careerDuration} years</p>
                          </div>

                          {/* Education Level */}
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center mb-2">
                              <AcademicCapIcon className="h-5 w-5 text-indigo-500 mr-2" />
                              <h4 className="text-lg font-medium text-gray-900">Education</h4>
                            </div>
                            <p className="text-2xl font-bold text-indigo-600">{results[selectedProfile]?.analysis.educationLevel}</p>
                          </div>

                          {/* Skill Categories */}
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center mb-2">
                              <ChartPieIcon className="h-5 w-5 text-indigo-500 mr-2" />
                              <h4 className="text-lg font-medium text-gray-900">Skill Categories</h4>
                            </div>
                            {Object.entries(results[selectedProfile]?.analysis.skillCategories || {}).map(([category, skills]: [string, string[]]) => (
                              <div key={category} className="mb-2">
                                <h5 className="text-sm font-medium text-gray-700 capitalize">{category}</h5>
                                <div className="flex flex-wrap gap-1">
                                  {skills.map((skill, index) => (
                                    <span 
                                      key={index}
                                      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800"
                                    >
                                      {skill}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Keyword Analysis */}
                          <div className="bg-gray-50 rounded-lg p-4">
                            <div className="flex items-center mb-2">
                              <ShieldCheckIcon className="h-5 w-5 text-indigo-500 mr-2" />
                              <h4 className="text-lg font-medium text-gray-900">Keyword Frequency</h4>
                            </div>
                            <div className="space-y-2">
                              {Object.entries(results[selectedProfile]?.analysis.keywordFrequency).map(([keyword, frequency]) => (
                                <div key={keyword} className="flex items-center justify-between">
                                  <span className="text-sm text-gray-700 capitalize">{keyword}</span>
                                  <span className="text-sm font-medium text-indigo-600">{frequency} mentions</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Industry Experience */}
                        <div className="mt-6">
                          <h4 className="text-lg font-medium text-gray-900 mb-2">Industry Experience</h4>
                          <div className="flex flex-wrap gap-2">
                              {results[selectedProfile]?.analysis.industryExperience.map((industry: string, index: number) => (
                              <span 
                                key={index}
                                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800"
                              >
                                {industry}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Top Skills */}
                        <div className="mt-6">
                          <h4 className="text-lg font-medium text-gray-900 mb-2">Top Skills</h4>
                          <div className="flex flex-wrap gap-2">
                              {results[selectedProfile]?.analysis.topSkills.map((skill: string, index: number) => (
                              <span 
                                key={index}
                                className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Connection Quality (if available) */}
                        {results[selectedProfile]?.analysis.connectionQuality && (
                          <div className="mt-6">
                            <h4 className="text-lg font-medium text-gray-900 mb-2">Network Analysis</h4>
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                              <div className="bg-gray-50 rounded-lg p-4">
                                <p className="text-sm text-gray-500">Relevant Connections</p>
                                <p className="text-2xl font-bold text-indigo-600">
                                  {results[selectedProfile]?.analysis.connectionQuality.relevantConnections}
                                </p>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-4">
                                <p className="text-sm text-gray-500">Industry Leaders</p>
                                <p className="text-2xl font-bold text-indigo-600">
                                  {results[selectedProfile]?.analysis.connectionQuality.industryLeaders}
                                </p>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-4">
                                <p className="text-sm text-gray-500">Same Industry</p>
                                <p className="text-2xl font-bold text-indigo-600">
                                  {results[selectedProfile]?.analysis.connectionQuality.sameIndustry}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="bg-white rounded-lg shadow-lg p-6 text-center text-gray-500">
                  Enter a search query or LinkedIn profile URL and click "Search & Find Emails" to see results here
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LinkedInScraper />
    </QueryClientProvider>
  )
}
